import React, { useEffect, useRef, useState } from 'react';

interface ScoreCelebrationCheckmarkProps {
  targetRef: React.RefObject<HTMLElement>;
  originRef?: React.RefObject<HTMLElement>;
  triggered: boolean;
  onComplete?: () => void;
}

type Stage = 'idle' | 'enter' | 'appear' | 'hold' | 'move' | 'flash' | 'done';

const APPEAR_MS = 200;
const HOLD_MS = 300;
const MOVE_MS = 600;
const FLASH_MS = 400;
const START_DELAY_MS = 20;
const CHECKMARK_SIZE_PX = 115; // w-24 (96px) + 20%
const CHECKMARK_FONT_PX = 58; // text-5xl (48px) + 20%

const ScoreCelebrationCheckmark: React.FC<ScoreCelebrationCheckmarkProps> = ({
  targetRef,
  originRef,
  triggered,
  onComplete,
}) => {
  const [stage, setStage] = useState<Stage>('idle');
  const [origin, setOrigin] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [offset, setOffset] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const [targetPoint, setTargetPoint] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const timeoutIdsRef = useRef<number[]>([]);

  // getBoundingClientRect returns viewport-relative pixel coordinates, which are
  // already correct in RTL layouts, so no left/right flipping logic is needed here.
  const computeTargetOffset = (): void => {
    const el = targetRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const targetX = rect.left + rect.width / 2;
    const targetY = rect.top + rect.height / 2;

    const originEl = originRef?.current;
    const originRect = originEl ? originEl.getBoundingClientRect() : null;
    const originX = originRect ? originRect.left + originRect.width / 2 : window.innerWidth / 2;
    const originY = originRect ? originRect.top + originRect.height / 2 : window.innerHeight / 2;

    setOrigin({ x: originX, y: originY });
    setOffset({ dx: targetX - originX, dy: targetY - originY });
    setTargetPoint({ x: targetX, y: targetY });
  };

  useEffect(() => {
    if (!triggered) return undefined;

    computeTargetOffset();
    setStage('enter');

    const schedule = (fn: () => void, delay: number): void => {
      timeoutIdsRef.current.push(window.setTimeout(fn, delay));
    };

    schedule(() => setStage('appear'), START_DELAY_MS);
    schedule(() => setStage('hold'), START_DELAY_MS + APPEAR_MS);
    schedule(() => setStage('move'), START_DELAY_MS + APPEAR_MS + HOLD_MS);
    schedule(() => setStage('flash'), START_DELAY_MS + APPEAR_MS + HOLD_MS + MOVE_MS);
    schedule(
      () => {
        setStage('done');
        onComplete?.();
      },
      START_DELAY_MS + APPEAR_MS + HOLD_MS + MOVE_MS + FLASH_MS,
    );

    return () => {
      timeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutIdsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggered]);

  // Target position can move (e.g. orientation change) before the checkmark
  // starts converging; the position is locked once the move stage begins.
  useEffect(() => {
    if (stage !== 'enter' && stage !== 'appear' && stage !== 'hold') return undefined;

    const handleResize = (): void => computeTargetOffset();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (stage === 'idle' || stage === 'done') return null;

  const isConverging = stage === 'move' || stage === 'flash';
  const translateX = isConverging ? offset.dx : 0;
  const translateY = isConverging ? offset.dy : 0;
  const scale = stage === 'enter' ? 0.3 : isConverging ? 0.25 : 1;
  const opacity = stage === 'enter' || stage === 'flash' ? 0 : 0.5;

  const transition =
    stage === 'appear'
      ? 'transform 200ms ease-out, opacity 200ms ease-out'
      : stage === 'move'
        ? 'transform 600ms cubic-bezier(0.65, 0, 0.35, 1)'
        : stage === 'flash'
          ? 'opacity 150ms ease-in'
          : 'none';

  return (
    <div className="fixed inset-0 z-[900] pointer-events-none" aria-hidden="true">
      <div
        className="fixed rounded-full flex items-center justify-center text-white font-bold shadow-lg"
        style={{
          left: origin.x,
          top: origin.y,
          width: CHECKMARK_SIZE_PX,
          height: CHECKMARK_SIZE_PX,
          fontSize: CHECKMARK_FONT_PX,
          background: 'var(--kol-success)',
          transform: `translate(-50%, -50%) translate(${translateX}px, ${translateY}px) scale(${scale})`,
          opacity,
          transition,
        }}
      >
        ✓
      </div>
      {stage === 'flash' && (
        <div
          className="fixed w-14 h-14 rounded-full"
          style={{
            left: targetPoint.x,
            top: targetPoint.y,
            background: 'var(--kol-success)',
            animation: 'kol-score-flash 400ms ease-out forwards',
          }}
        />
      )}
    </div>
  );
};

export default ScoreCelebrationCheckmark;
